import axios from 'axios';
import type { Task } from '../types/Task';

const API_URL = process.env.REACT_APP_API_URL || '';

interface TaskPayload {
  description: string;
  name?: string;
  instructions?: string;
  type: string;
  projectId: number;
  status?: string;
  identifiedDate?: string;
  completionDate?: string;
  completionAccomplishment?: number;
  taskType?: string;
  costType?: string;
  rateAdjustmentComments?: string;
  labourMarkup?: number;
  materialMarkup?: number;
  subcontractorMarkup?: number;
  vehicleMarkup?: number;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  assetId?: number;
  assetType?: string;
  total?: number;
  isOverhead?: boolean;
  activityId?: number;
  foremanAreaId?: number;
}

const taskService = {
  getAllTasks: async (): Promise<Task[]> => {
    const response = await axios.get(`${API_URL}/tasks/task`);
    return response.data;
  },

  getLightTasks: async (): Promise<Task[]> => {
    const response = await axios.get(`${API_URL}/tasks/task/light`);
    return response.data;
  },

  // Minimal active quantified tasks for forms dropdown
  getQuantifiedDropdownOptions: async (): Promise<Task[]> => {
    const response = await axios.get(`${API_URL}/tasks/quantified/dropdown-options`);
    return response.data;
  },

  // Typeahead search for dropdown (tasks of any type and status)
  // `excludeStandingTypes` is opt-in: pass e.g. ['Routine','Quantified'] to leave those types'
  // standing tasks out. Callers that omit it get the unfiltered list they always got.
  searchActiveTasks: async (
    q: string,
    limit: number = 10,
    options?: { excludeStandingTypes?: string[]; effortEligibleOnly?: boolean }
  ): Promise<Task[]> => {
    const excludeStandingTypes = options?.excludeStandingTypes?.length
      ? options.excludeStandingTypes.join(',')
      : undefined;
    const response = await axios.get(`${API_URL}/tasks/task/search`, {
      params: {
        q,
        limit,
        ...(excludeStandingTypes ? { excludeStandingTypes } : {}),
        // H2: only statuses that can receive effort (server-configured list).
        ...(options?.effortEligibleOnly ? { effortEligibleOnly: 'true' } : {})
      }
    });
    return response.data;
  },

  // Search tasks eligible for Work ID assignment (PROPOSED, PLANNED, ACTIVE only)
  searchAssignableTasks: async (q: string, limit: number = 10): Promise<Task[]> => {
    const response = await axios.get(`${API_URL}/tasks/task/search`, {
      params: { q, limit, statuses: 'PROPOSED,PLANNED,ACTIVE' }
    });
    return response.data;
  },

  getLightTasksByIds: async (taskIds: number[]): Promise<Task[]> => {
    if (taskIds.length === 0) {
      return [];
    }
    const taskIdsParam = taskIds.join(',');
    const response = await axios.get(`${API_URL}/tasks/task/light/by-ids?taskIds=${taskIdsParam}`);
    return response.data;
  },

  getTask: async (taskId: number): Promise<Task> => {
    const response = await axios.get(`${API_URL}/tasks/task/${taskId}`);
    return response.data;
  },

  getTasksByModeAndType: async (mode: string, type: string): Promise<Task[]> => {
    try {
      const response = await axios.get(`${API_URL}/tasks/task/mode/${mode}/type/${type}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks by mode and type:', error);
      throw error;
    }
  },

  createTask: async (payload: TaskPayload): Promise<Task> => {
    if (payload.type === 'H200') {
      payload.status = payload.status === 'CLOSED' ? 'CLOSED' : 'ACTIVE';
    }
    if (payload.isOverhead) {
      payload.type = 'Overhead';
    }
    if (payload.type === 'Custom') {
      payload.status = payload.status || 'ACTIVE';
    }
    const response = await axios.post(`${API_URL}/tasks/task`, payload);
    return response.data;
  },

  updateTask: async (taskId: string, payload: TaskPayload): Promise<Task> => {
    if (payload.type === 'H200') {
      payload.status = payload.status === 'CLOSED' ? 'CLOSED' : 'ACTIVE';
    }
    if (payload.type === 'Custom') {
      payload.status = payload.status || 'ACTIVE';
    }
    const response = await axios.patch(`${API_URL}/tasks/task/${taskId}`, payload);
    return response.data;
  },

  deleteTask: async (taskId: string): Promise<void> => {
    await axios.delete(`${API_URL}/tasks/task/${taskId}`);
  },

  cancelTask: async (taskId: string, comments: string, userId?: number): Promise<Task> => {
    const response = await axios.post(`${API_URL}/tasks/task/${taskId}/cancel`, {
      comments,
      userId
    });
    return response.data;
  },

  convertTaskType: async (
    taskId: number,
    body: { targetType: 'Quantified' | 'Custom'; unitOfMeasure?: string; identifiedQuantity?: number }
  ): Promise<Task> => {
    const response = await axios.post(`${API_URL}/tasks/task/${taskId}/convert-type`, body);
    return response.data;
  },

  convertTaskToRoutine: async (taskId: number): Promise<Task> => {
    const response = await axios.post(`${API_URL}/tasks/task/${taskId}/convert-to-routine`);
    return response.data;
  },

  /** Clone task to a new ID: same setup, no efforts or old history; new task history notes source. */
  cloneTask: async (
    taskId: number,
    options?: { name?: string; contractYear?: string; status?: string }
  ): Promise<Task> => {
    try {
      const body: { name?: string; contractYear?: string; status?: string } = {};
      if (options?.name != null && options.name.trim() !== '') {
        body.name = options.name.trim();
      }
      if (options?.contractYear != null && options.contractYear.trim() !== '') {
        body.contractYear = options.contractYear.trim();
      }
      if (options?.status != null && options.status.trim() !== '') {
        body.status = options.status.trim();
      }
      const response = await axios.post(
        `${API_URL}/tasks/task/${taskId}/clone`,
        body,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const d = error.response.data as { error?: string; details?: string };
        throw new Error(d.error || d.details || 'Failed to clone task');
      }
      throw error;
    }
  }
};

export { taskService, type TaskPayload }; 